import { useDisclosure } from "@mantine/hooks";
import uniq from "lodash/uniq";
import { DateTime } from "luxon";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CompanySettings } from "#modules/company/infra/CompanySettings.dto";
import { MaterialFormModal } from "#modules/company/view/MaterialFormModal";
import { MaterialsSectionTable } from "#modules/company/view/MaterialsSectionTable";
import { makeCustomMaterialsCsv } from "#modules/materials/domain/makeCustomMaterialsCsv";
import { Material } from "#modules/materials/infra/fetchCustomMaterials.api";
import { useCustomMaterials } from "#modules/materials/view/useCustomMaterials";
import { useDeleteCustomMaterials } from "#modules/materials/view/useDeleteCustomMaterials";
import { useDeleteMaterial } from "#modules/materials/view/useDeleteMaterial";
import { useUploadCustomMaterials } from "#modules/materials/view/useUploadCustomMaterials";
import { makeDownloadableUrl } from "#shared/helpers/file";
import { Icon } from "#shared/icons/Icon";
import { Alert } from "#shared/view/components/Alert";
import { Anchor } from "#shared/view/components/Anchor";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { Space } from "#shared/view/components/Space";
import { Button } from "#shared/view/designSystem/Button";
import { FileButton } from "#shared/view/designSystem/FileButton";
import { SelectInput } from "#shared/view/designSystem/SelectInput";
import { MaterialUploadError, MaterialPriceAlert } from "shared";

import { DeleteMaterialsConfirmationModal } from "./DeleteMaterialsConfirmationModal";
import { MaterialsCsvDetailsModal } from "./MaterialsCsvDetailsModal";
import { MaterialsPriceAlertModal } from "./MaterialsPriceAlertModal";

type Props = {
  uploadErrors: MaterialUploadError[];
  setUploadErrors: Dispatch<SetStateAction<MaterialUploadError[]>>;
};

export const MaterialsPriceTable = ({ setUploadErrors }: Props) => {
  const { t } = useTranslation();

  const { customMaterials } = useCustomMaterials({});
  const importMaterialsButtonRef = useRef<() => void>(null);

  const [
    isExplicationModaleOpened,
    { close: closeExplicationModale, open: openExplicationModale },
  ] = useDisclosure(false);
  const [
    isDeleteConfirmationModalOpened,
    { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal },
  ] = useDisclosure(false);
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] =
    useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [isPriceAlertModalOpened, { open: openPriceAlertModal, close: closePriceAlertModal }] =
    useDisclosure(false);
  const [priceAlerts, setPriceAlerts] = useState<MaterialPriceAlert[]>([]);

  const { uploadCustomMaterials } = useUploadCustomMaterials(
    (errors) => {
      setUploadErrors(errors);
    },
    (alerts) => {
      setPriceAlerts(alerts);
      openPriceAlertModal();
    },
  );

  const { deleteCustomMaterials } = useDeleteCustomMaterials();
  const { deleteMaterial } = useDeleteMaterial();

  const onAddRow = () => {
    setSelectedMaterial(null);
    openCreateModal();
  };

  const onEditRow = (rowId: string) => {
    const material = customMaterials.find((item) => item.id === rowId) ?? null;
    setSelectedMaterial(material);
    openEditModal();
  };

  const onRemoveRow = (rowId: string) => {
    deleteMaterial({ id: rowId });
  };

  const uploadMaterialsCsv = (file: File | null) => {
    if (!file) {
      return;
    }

    setUploadErrors([]);
    uploadCustomMaterials(file);
    importMaterialsButtonRef.current?.();
  };

  const makeCsvUrl = () => {
    const csvData = makeCustomMaterialsCsv(customMaterials);

    return makeDownloadableUrl(csvData, "text/csv");
  };

  const downloadButtonLabel =
    customMaterials.length === 0 ? "Télécharger le modèle" : "Télécharger le fichier";

  const {
    field: { value: defaultSheetFormat, onChange: onDefaultSheetFormatChange },
  } = useController<CompanySettings, "defaultSheetFormat">({ name: "defaultSheetFormat" });

  const uniqSheetFormats = uniq(
    customMaterials
      .filter((material) => material.type === "SHEET")
      .sort((a, b) => {
        if (a.length === b.length) {
          return a.width - b.width;
        }
        return a.length - b.length;
      })
      .map((material) => `${material.length}x${material.width}`),
  );

  const selectedSheetFormat = formatSheetFormatToString(defaultSheetFormat);

  const isSelectedSheetFormatUnavailable =
    selectedSheetFormat && !uniqSheetFormats.includes(selectedSheetFormat);

  const uniqSheetFormatsOptions = [
    ...uniqSheetFormats.map((format) => ({ label: format, value: format })),
    ...(isSelectedSheetFormatUnavailable
      ? [{ label: selectedSheetFormat, value: selectedSheetFormat }]
      : []),
  ];

  return (
    <>
      <Group>
        <FileButton
          resetRef={importMaterialsButtonRef}
          onChange={uploadMaterialsCsv}
          accept="text/csv"
        >
          {(props) => (
            <Button variant="outline" icon={<Icon name="upload" />} {...props}>
              Importer un fichier
            </Button>
          )}
        </FileButton>
        <Anchor
          href={makeCsvUrl()}
          target="_blank"
          rel="noopener noreferrer"
          download={`export-fabera-${DateTime.now().toISODate()}.csv`}
        >
          <Button variant="outline" icon={<Icon name="download" />}>
            {downloadButtonLabel}
          </Button>
        </Anchor>
        <Button variant="subtle" leftSection={<Icon name="help" />} onClick={openExplicationModale}>
          Comment remplir le fichier
        </Button>
      </Group>
      {customMaterials.length > 0 && (
        <>
          <Space h="sm" />
          <Group>
            <Button
              variant="light"
              color="red"
              icon={<Icon name="trash" />}
              onClick={openDeleteConfirmationModal}
            >
              {t("settings.materials.deleteAllMaterials")}
            </Button>
          </Group>
        </>
      )}
      <Space h="sm" />
      <Box miw={400} maw={1400} w="100%">
        <MaterialsSectionTable
          data={customMaterials}
          onAddRow={onAddRow}
          onEditRow={onEditRow}
          onRemoveRow={onRemoveRow}
        />
      </Box>

      <Space h="sm" />
      <Flex maw={400} direction="column" gap="xs">
        <SelectInput
          name="defaultSheetFormat"
          data={uniqSheetFormatsOptions}
          label={t("settings.materials.defaultSheetFormat.label")}
          value={selectedSheetFormat}
          onChange={(value) => onDefaultSheetFormatChange(parseSheetFormatString(value))}
          withEmptyData={false}
          clearable
          searchable
        />
        {isSelectedSheetFormatUnavailable && (
          <Alert title={t("settings.materials.defaultSheetFormat.unavailable")} color="red">
            {t("settings.materials.defaultSheetFormat.unavailableDescription")}
          </Alert>
        )}
      </Flex>

      <MaterialsCsvDetailsModal opened={isExplicationModaleOpened} close={closeExplicationModale} />

      <DeleteMaterialsConfirmationModal
        opened={isDeleteConfirmationModalOpened}
        onClose={closeDeleteConfirmationModal}
        onDelete={deleteCustomMaterials}
      />

      <MaterialFormModal opened={createModalOpened} onClose={closeCreateModal} mode="create" />
      {selectedMaterial && (
        <MaterialFormModal
          opened={editModalOpened}
          onClose={closeEditModal}
          mode="edit"
          initialData={selectedMaterial}
        />
      )}

      <MaterialsPriceAlertModal
        opened={isPriceAlertModalOpened}
        close={closePriceAlertModal}
        alerts={priceAlerts}
      />
    </>
  );
};

type SheetFormat = { length: number; width: number };

const formatSheetFormatToString = (format: unknown): string | null => {
  if (
    typeof format !== "object" ||
    format === null ||
    !("length" in format) ||
    !("width" in format)
  ) {
    return null;
  }

  const { length, width } = format as SheetFormat;
  if (typeof length !== "number" || typeof width !== "number") {
    return null;
  }

  return `${length}x${width}`;
};

const parseSheetFormatString = (value: string | null): SheetFormat | null => {
  if (!value) {
    return null;
  }

  const [lengthString, widthString] = value.split("x");
  const length = Number(lengthString);
  const width = Number(widthString);
  if (Number.isNaN(length) || Number.isNaN(width)) {
    return null;
  }

  return { length, width };
};
